import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  Filter,
  X,
  Phone,
  Mail,
  Clock,
  CheckCircle2,
  Inbox,
  Calendar as CalendarIcon,
  ArrowDownUp,
  MessageSquare,
  Archive,
  Loader2,
} from 'lucide-react';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { PageMeta } from '@/components/PageMeta';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { API } from '@/lib/api';
import { formatApiError } from '@/components/ProtectedRoute';

const STATUS_META = {
  new:      { label: 'Nouveau',  badge: 'bg-amber-500/15 text-amber-700 border-amber-500/30',     dot: 'bg-amber-500' },
  called:   { label: 'Rappelé',  badge: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30', dot: 'bg-emerald-500' },
  emailed:  { label: 'Mailé',    badge: 'bg-blue-500/15 text-blue-700 border-blue-500/30',         dot: 'bg-blue-500' },
  other:    { label: 'Autre',    badge: 'bg-purple-500/15 text-purple-700 border-purple-500/30',   dot: 'bg-purple-500' },
  archived: { label: 'Archivé',  badge: 'bg-gray-300/40 text-gray-600 border-gray-400/30',          dot: 'bg-gray-400' },
};
const STATUS_FILTERS = [
  { value: 'all',      label: 'Toutes' },
  { value: 'new',      label: 'Nouvelles' },
  { value: 'called',   label: 'Rappelées' },
  { value: 'emailed',  label: 'Mailées' },
  { value: 'other',    label: 'Autres' },
  { value: 'archived', label: 'Archivées' },
];

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};
const fmtDateShort = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffH = (now - d) / 36e5;
    if (diffH < 24) return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
  } catch { return iso; }
};

export default function AdminContactsPage() {
  const [items, setItems] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selected, setSelected] = useState(null);
  const [updating, setUpdating] = useState(null);

  const load = async () => {
    setRefreshing(true);
    try {
      const { data } = await axios.get(`${API}/admin/contact/list`, { params: { limit: 200 } });
      setItems(data || []);
    } catch (err) {
      toast.error(formatApiError(err));
      setItems([]);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!items) return [];
    let out = items;
    // Status filter
    if (statusFilter !== 'all') {
      out = out.filter((c) => (c.handled_status || 'new') === statusFilter);
    }
    // Date range
    if (dateFrom) {
      const fromTs = new Date(dateFrom + 'T00:00:00').getTime();
      out = out.filter((c) => c.created_at && new Date(c.created_at).getTime() >= fromTs);
    }
    if (dateTo) {
      const toTs = new Date(dateTo + 'T23:59:59').getTime();
      out = out.filter((c) => c.created_at && new Date(c.created_at).getTime() <= toTs);
    }
    // Search
    if (search.trim()) {
      const q = search.toLowerCase();
      out = out.filter((c) =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.email || '').toLowerCase().includes(q) ||
        (c.phone || '').toLowerCase().includes(q) ||
        (c.message || '').toLowerCase().includes(q)
      );
    }
    // Sort newest first (defensive — backend already sorts)
    return [...out].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  }, [items, search, statusFilter, dateFrom, dateTo]);

  const counts = useMemo(() => {
    const c = { all: items?.length || 0, new: 0, called: 0, emailed: 0, other: 0, archived: 0 };
    (items || []).forEach((it) => { c[it.handled_status || 'new'] = (c[it.handled_status || 'new'] || 0) + 1; });
    return c;
  }, [items]);

  const updateStatus = async (id, status, note = null) => {
    setUpdating(id + status);
    try {
      const payload = { status };
      if (note !== null && note !== undefined) payload.note = note;
      await axios.patch(`${API}/admin/contact/${id}`, payload);
      toast.success(`Marqué « ${STATUS_META[status]?.label || status} »`);
      setItems((prev) => prev.map((it) => it.id === id ? {
        ...it,
        handled_status: status,
        handled_at: status === 'new' ? null : new Date().toISOString(),
        ...(note !== null && note !== undefined ? { handled_note: note } : {}),
      } : it));
      setSelected((s) => s && s.id === id ? {
        ...s,
        handled_status: status,
        handled_at: status === 'new' ? null : new Date().toISOString(),
        ...(note !== null && note !== undefined ? { handled_note: note } : {}),
      } : s);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUpdating(null);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('all');
    setDateFrom('');
    setDateTo('');
  };
  const hasFilters = search || statusFilter !== 'all' || dateFrom || dateTo;

  return (
    <AdminLayout>
      <PageMeta title="Demandes - Admin" description="Gestion des demandes de contact" robots="noindex,nofollow" />

      <div className="mb-6">
        <h1 className="font-heading text-3xl text-dark mb-1 tracking-wide">DEMANDES DE CONTACT</h1>
        <p className="text-sm text-muted-foreground">
          Toutes les demandes reçues, triées du plus récent au plus ancien.
          {items !== null && (
            <span className="ml-2">
              <strong className="text-dark">{counts.all}</strong> au total ·
              <strong className="text-amber-700 ml-1">{counts.new}</strong> à traiter
            </span>
          )}
        </p>
      </div>

      {/* Filters */}
      <Card className="p-4 sm:p-5 mb-5" data-testid="contacts-filters">
        <div className="flex flex-col gap-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              placeholder="Rechercher par nom, email, téléphone ou message…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="contacts-search"
              className="w-full pl-10 pr-10 py-2.5 border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/60 transition-all"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-dark"
                aria-label="Effacer"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {/* Status filters */}
            <div className="flex flex-wrap gap-1.5 flex-1">
              {STATUS_FILTERS.map((f) => {
                const active = statusFilter === f.value;
                const count = counts[f.value] || 0;
                return (
                  <button
                    key={f.value}
                    onClick={() => setStatusFilter(f.value)}
                    data-testid={`contacts-filter-${f.value}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                      active
                        ? 'bg-dark text-white border-dark'
                        : 'bg-white text-muted-foreground border-border hover:border-dark/40 hover:text-dark'
                    }`}
                  >
                    {f.label}
                    <span className={`ml-1.5 text-[10px] tabular-nums ${active ? 'text-white/70' : 'text-muted-foreground/70'}`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Date range */}
            <div className="flex items-center gap-2 text-xs">
              <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground" />
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                data-testid="contacts-date-from"
                className="border border-border rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary/40"
                aria-label="Date de début"
              />
              <span className="text-muted-foreground">→</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                data-testid="contacts-date-to"
                className="border border-border rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary/40"
                aria-label="Date de fin"
              />
            </div>

            {hasFilters && (
              <Button size="sm" variant="ghost" onClick={clearFilters} className="text-xs gap-1.5" data-testid="contacts-clear-filters">
                <X className="h-3 w-3" /> Effacer
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={load} disabled={refreshing} className="text-xs gap-1.5" data-testid="contacts-refresh">
              <Loader2 className={`h-3 w-3 ${refreshing ? 'animate-spin' : ''}`} /> Rafraîchir
            </Button>
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden" data-testid="contacts-table-card">
        {items === null ? (
          <div className="flex items-center justify-center h-40 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 px-4" data-testid="contacts-empty">
            <Inbox className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
            <p className="font-semibold text-dark text-sm">
              {hasFilters ? 'Aucune demande ne correspond à ta recherche' : 'Aucune demande pour le moment'}
            </p>
            {hasFilters && (
              <button onClick={clearFilters} className="text-xs text-primary hover:underline mt-2" data-testid="contacts-empty-reset">
                Effacer les filtres
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Desktop table header */}
            <div className="hidden md:grid grid-cols-[1.4fr_1.6fr_1fr_2fr_1fr_0.7fr] gap-3 px-5 py-3 bg-accent/40 border-b border-border text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              <div className="flex items-center gap-1.5">Nom <ArrowDownUp className="h-3 w-3 opacity-40" /></div>
              <div>Contact</div>
              <div>Téléphone</div>
              <div>Message</div>
              <div>Reçu le</div>
              <div className="text-right">Statut</div>
            </div>

            <ul className="divide-y divide-border" data-testid="contacts-list">
              <AnimatePresence>
                {filtered.map((c) => {
                  const status = c.handled_status || 'new';
                  const meta = STATUS_META[status] || STATUS_META.new;
                  return (
                    <motion.li
                      key={c.id}
                      layout
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0, height: 0 }}
                      data-testid={`contact-row-${c.id}`}
                      onClick={() => setSelected(c)}
                      className={`group cursor-pointer transition-colors ${
                        status === 'new' ? 'bg-amber-50/30 hover:bg-amber-50/60' : 'hover:bg-accent/40'
                      }`}
                    >
                      {/* Mobile: card */}
                      <div className="md:hidden p-4 flex flex-col gap-1.5">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                          <span className="font-semibold text-sm text-dark truncate flex-1">{c.name}</span>
                          <span className="text-[10px] text-muted-foreground">{fmtDateShort(c.created_at)}</span>
                        </div>
                        {c.phone && <div className="text-xs text-muted-foreground">{c.phone}</div>}
                        <div className="text-xs text-muted-foreground line-clamp-1">{c.message}</div>
                        <Badge variant="outline" className={`${meta.badge} self-start text-[10px] border`}>{meta.label}</Badge>
                      </div>

                      {/* Desktop: row */}
                      <div className="hidden md:grid grid-cols-[1.4fr_1.6fr_1fr_2fr_1fr_0.7fr] gap-3 px-5 py-3.5 items-center">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${meta.dot}`} />
                          <span className="font-semibold text-sm text-dark truncate">{c.name}</span>
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {c.email && !/(\+|@cfsc\.fr$)/i.test(c.email) ? c.email : <span className="italic">—</span>}
                        </div>
                        <div className="text-xs text-muted-foreground truncate font-mono">{c.phone || '—'}</div>
                        <div className="text-xs text-dark/70 line-clamp-1">{c.message}</div>
                        <div className="text-xs text-muted-foreground tabular-nums">{fmtDateShort(c.created_at)}</div>
                        <div className="text-right">
                          <Badge variant="outline" className={`${meta.badge} text-[10px] border`}>{meta.label}</Badge>
                        </div>
                      </div>
                    </motion.li>
                  );
                })}
              </AnimatePresence>
            </ul>
          </>
        )}
      </Card>

      {/* Detail Modal */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl" data-testid="contact-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3 mb-1">
                  <div className={`w-2 h-2 rounded-full ${STATUS_META[selected.handled_status || 'new'].dot}`} />
                  <DialogTitle className="text-xl">{selected.name}</DialogTitle>
                  <Badge variant="outline" className={`${STATUS_META[selected.handled_status || 'new'].badge} text-[10px] border`}>
                    {STATUS_META[selected.handled_status || 'new'].label}
                  </Badge>
                </div>
                <DialogDescription className="flex items-center gap-2 text-xs">
                  <Clock className="h-3 w-3" /> Reçue le {fmtDate(selected.created_at)}
                  {selected.handled_at && (
                    <>
                      <span className="text-muted-foreground/50">·</span>
                      <span className="text-emerald-700">Traitée le {fmtDate(selected.handled_at)}</span>
                    </>
                  )}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 mt-2">
                {/* Quick contact */}
                <div className="flex flex-wrap gap-2">
                  {selected.phone && (
                    <a
                      href={`tel:${selected.phone.replace(/\s/g, '')}`}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold transition-colors"
                      data-testid="contact-detail-call"
                    >
                      <Phone className="h-4 w-4" /> Appeler {selected.phone}
                    </a>
                  )}
                  {selected.email && !/(\+|@cfsc\.fr$)/i.test(selected.email) && (
                    <a
                      href={`mailto:${selected.email}`}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors"
                      data-testid="contact-detail-email"
                    >
                      <Mail className="h-4 w-4" /> Répondre par mail
                    </a>
                  )}
                </div>

                {/* Message */}
                <div className="rounded-xl bg-accent/40 border border-border p-4">
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                    <MessageSquare className="h-3 w-3" /> Message
                  </div>
                  <p className="text-sm text-dark whitespace-pre-wrap leading-relaxed" data-testid="contact-detail-message">
                    {selected.message}
                  </p>
                </div>

                {/* Note (if any) */}
                {selected.handled_note && (
                  <div className="rounded-xl bg-emerald-50 border border-emerald-500/20 p-3">
                    <div className="text-[10px] uppercase tracking-widest text-emerald-700 mb-1">Note interne</div>
                    <p className="text-xs text-emerald-900">{selected.handled_note}</p>
                  </div>
                )}

                {/* Metadata */}
                <div className="grid grid-cols-2 gap-3 text-xs pt-2 border-t border-border">
                  {selected.email && (
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">Email</div>
                      <div className="text-dark truncate">{selected.email}</div>
                    </div>
                  )}
                  {selected.phone && (
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">Téléphone</div>
                      <div className="text-dark font-mono">{selected.phone}</div>
                    </div>
                  )}
                </div>
              </div>

              <DialogFooter className="flex-col sm:flex-row gap-2 mt-4 pt-4 border-t border-border">
                {(selected.handled_status || 'new') === 'new' ? (
                  <>
                    <Button
                      onClick={() => updateStatus(selected.id, 'called')}
                      disabled={updating === selected.id + 'called'}
                      data-testid="contact-detail-mark-called"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg gap-1.5"
                    >
                      <Phone className="h-3.5 w-3.5" /> Marquer rappelée
                    </Button>
                    <Button
                      onClick={() => updateStatus(selected.id, 'emailed')}
                      disabled={updating === selected.id + 'emailed'}
                      data-testid="contact-detail-mark-emailed"
                      className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg gap-1.5"
                    >
                      <Mail className="h-3.5 w-3.5" /> Marquer mailée
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => updateStatus(selected.id, 'other')}
                      disabled={updating === selected.id + 'other'}
                      data-testid="contact-detail-mark-other"
                      className="rounded-lg gap-1.5"
                    >
                      <CheckCircle2 className="h-3.5 w-3.5" /> Autre / Traitée
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => updateStatus(selected.id, 'archived')}
                      disabled={updating === selected.id + 'archived'}
                      data-testid="contact-detail-mark-archived"
                      className="text-muted-foreground gap-1.5 ml-auto"
                    >
                      <Archive className="h-3.5 w-3.5" /> Archiver
                    </Button>
                  </>
                ) : (
                  <>
                    <span className="text-xs text-emerald-700 inline-flex items-center gap-1.5 font-semibold">
                      <CheckCircle2 className="h-4 w-4" /> Demande déjà traitée
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => updateStatus(selected.id, 'new')}
                      disabled={updating === selected.id + 'new'}
                      data-testid="contact-detail-mark-new"
                      className="ml-auto text-xs"
                    >
                      Remettre en attente
                    </Button>
                  </>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
